import { useMemo } from 'react';
import { X } from 'lucide-react';
import clsx from 'clsx';
import { useBatch } from '../../store/batchStore';
import type { Variant } from '../../bulk/types';

/**
 * Side-by-side comparison of two to four variants.
 *
 * The diff table only lists rows where the values actually differ. Showing every
 * parameter would bury the two or three that explain why one variant works and another
 * does not — which is the entire reason to compare them.
 */
export function CompareView(): JSX.Element | null {
  const variants = useBatch((s) => s.variants);
  const compareIds = useBatch((s) => s.compareIds);
  const toggleCompare = useBatch((s) => s.toggleCompare);

  const chosen = useMemo(
    () => compareIds.map((id) => variants.find((v) => v.id === id)).filter((v): v is Variant => Boolean(v)),
    [compareIds, variants],
  );

  const rows = useMemo(() => {
    if (chosen.length < 2) return [];
    const keys = new Set<string>();
    for (const v of chosen) for (const key of Object.keys(v.meta.dimensions)) keys.add(key);

    const base: { label: string; values: string[] }[] = [
      { label: 'Seed', values: chosen.map((v) => String(v.meta.seed)) },
      { label: 'Wort', values: chosen.map((v) => v.meta.word) },
      { label: 'Schrift', values: chosen.map((v) => v.meta.fontId) },
      { label: 'Techniken', values: chosen.map((v) => v.meta.techniques.join(' + ') || '—') },
      { label: 'Palette', values: chosen.map((v) => v.meta.palette) },
      { label: 'Canvas', values: chosen.map((v) => v.meta.canvas) },
    ];
    for (const key of keys) {
      base.push({
        label: key,
        values: chosen.map((v) => {
          const value = v.meta.dimensions[key];
          if (value === undefined) return '—';
          return typeof value === 'number' ? String(Number(value.toFixed(4))) : String(value);
        }),
      });
    }
    return base.filter((row) => new Set(row.values).size > 1);
  }, [chosen]);

  if (chosen.length < 2) return null;

  return (
    <div className="flex shrink-0 flex-col border-t border-ink-800 bg-ink-900" style={{ maxHeight: '46%' }}>
      <div className="flex items-center gap-2 border-b border-ink-800 px-3 py-2">
        <span className="panel-label flex-1">Vergleich — {rows.length} Unterschiede</span>
        <button
          type="button"
          onClick={() => chosen.forEach((v) => toggleCompare(v.id))}
          className="ghost-button h-6 w-6 p-0"
          title="Vergleich schliessen"
        >
          <X size={12} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="flex gap-2 p-3" style={{ minWidth: 'min-content' }}>
          {chosen.map((variant, i) => (
            <div key={variant.id} className="w-[190px] shrink-0">
              <div className="relative aspect-square overflow-hidden rounded border border-ink-700 bg-ink-950">
                {variant.url && <img src={variant.url} alt="" className="h-full w-full object-contain" />}
                <button
                  type="button"
                  onClick={() => toggleCompare(variant.id)}
                  className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded bg-ink-950/70 text-ink-300 hover:text-ink-100"
                  title="Aus dem Vergleich entfernen"
                >
                  <X size={10} />
                </button>
              </div>
              <div className="pt-1 text-center font-mono text-[9px] text-ink-500">
                {String(variant.meta.index).padStart(3, '0')} · Spalte {i + 1}
              </div>
            </div>
          ))}
        </div>

        {rows.length > 0 ? (
          <table className="w-full border-collapse font-mono text-[10px]">
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} className="border-t border-ink-850">
                  <td className="w-[180px] px-3 py-1 align-top text-ink-500">{row.label}</td>
                  {row.values.map((value, i) => (
                    <td
                      key={i}
                      className={clsx(
                        'px-3 py-1 align-top',
                        value === row.values[0] ? 'text-ink-400' : 'text-accent-500',
                      )}
                    >
                      {value}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="px-3 pb-3 text-[10px] text-ink-500">
            Diese Varianten unterscheiden sich in keinem erfassten Parameter.
          </p>
        )}
      </div>
    </div>
  );
}
